import { clamp, preventDefault, isMobileBreakpoint } from './fc-utils.js';

const MIN_Z = 1;
const MAX_Z = 5;
const START_Z = 1.5;

// Extract {x,y} from a Touch object
function touchXY(t) {
  if (!(t instanceof Touch)) return undefined;
  const { clientX: x, clientY: y } = t;
  return isFinite(x) && isFinite(y) ? { x, y } : undefined;
}

// Euclidean distance between two points
function dist(a, b) {
  if (!a || !b) return 0;
  const dx = a.x - b.x,
    dy = a.y - b.y;
  const d = Math.sqrt(dx * dx + dy * dy);
  return isFinite(d) ? d : 0;
}

// Midpoint between two points
function mid(a, b) {
  if (!a || !b) return { x: 0, y: 0 };
  const mx = (a.x + b.x) / 2,
    my = (a.y + b.y) / 2;
  return { x: isFinite(mx) ? mx : 0, y: isFinite(my) ? my : 0 };
}

const fin = (v) => isFinite(v);
const pos = (v) => fin(v) && v > 0;
const safePan = (v, fallback = 0) => (fin(v) ? v : fallback);

export class TouchZoomContainer extends HTMLElement {
  #ac = null;
  #ro = null;
  #zoom = START_Z;
  #initDist = 0;
  #initZoom = START_Z;
  #pan = { x: 0, y: 0 };
  #dragPt = { x: 0, y: 0 };
  #dragPan = { x: 0, y: 0 };
  #isDrag = false;
  #ready = false;
  #raf = null;

  get #img() {
    const el = this.querySelector('img');
    return el instanceof HTMLImageElement ? el : null;
  }

  connectedCallback() {
    if (!this.#img) return;
    this.#initResize();
    if (typeof isMobileBreakpoint !== 'function' || !isMobileBreakpoint()) return;
    this.#initTouch();
    this.#render();
  }

  #initResize() {
    if (typeof ResizeObserver === 'undefined') return;
    this.#ro ??= new ResizeObserver(this.#onResize);
    this.#ro.observe(this);
  }

  #initTouch() {
    if (this.#ready || !this.#img) return;
    this.#ready = true;
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const opts = { passive: false, signal };
    this.addEventListener('touchstart', this.#onStart, opts);
    this.addEventListener('touchmove', this.#onMove, opts);
    this.addEventListener('touchend', this.#onEnd, opts);
    this.#img.addEventListener('load', this.#render, { signal, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
    this.#ro?.disconnect();
    this.#ro = null;
    this.#dropFrame();
  }

  #onResize = () => {
    if (!this.#ready && typeof isMobileBreakpoint === 'function' && isMobileBreakpoint()) {
      this.#initTouch();
    }
    if (this.#ready) this.#queueRender();
  };

  #onStart = (e) => {
    if (!(e instanceof TouchEvent)) return;
    preventDefault(e);
    const { touches } = e;

    if (touches.length === 2) {
      const [t0, t1] = [touchXY(touches[0]), touchXY(touches[1])];
      if (!t0 || !t1) return;
      const d = dist(t0, t1);
      if (!pos(d)) return;
      this.#initDist = d;
      this.#initZoom = this.#zoom;
      this.#isDrag = false;
    } else if (touches.length === 1) {
      const c = touchXY(touches[0]);
      if (!c) return;
      this.#dragPt = { x: c.x, y: c.y };
      this.#dragPan = { x: this.#pan.x, y: this.#pan.y };
      this.#isDrag = true;
    }
  };

  #onMove = (e) => {
    if (!(e instanceof TouchEvent)) return;
    preventDefault(e);
    const { touches } = e;

    if (touches.length === 2) {
      this.#doPinch(e);
    } else if (touches.length === 1 && this.#isDrag) {
      this.#doDrag(e);
    }
  };

  #doPinch(e) {
    if (e.touches.length !== 2) return;
    const [c1, c2] = [touchXY(e.touches[0]), touchXY(e.touches[1])];
    if (!c1 || !c2 || !pos(this.#initDist)) return;

    const center = mid(c1, c2);
    const curDist = dist(c1, c2);
    if (!pos(curDist)) return;

    const prevZ = this.#zoom;
    this.#zoom = clamp((curDist / this.#initDist) * this.#initZoom, MIN_Z, MAX_Z);
    if (!fin(this.#zoom)) this.#zoom = MIN_Z;

    const cw = this.clientWidth,
      ch = this.clientHeight;
    if (!pos(cw) || !pos(ch)) return;

    const offX = center.x - cw / 2;
    const offY = center.y - ch / 2;
    const ratio = this.#zoom / prevZ - 1;

    this.#pan.x -= (offX * ratio) / this.#zoom;
    this.#pan.y -= (offY * ratio) / this.#zoom;
    this.#pan.x = safePan(this.#pan.x);
    this.#pan.y = safePan(this.#pan.y);

    this.#queueRender();
    this.#isDrag = false;
  }

  #doDrag(e) {
    if (e.touches.length !== 1) return;
    const c = touchXY(e.touches[0]);
    if (!c || !pos(this.#zoom)) return;

    const dx = (c.x - this.#dragPt.x) / this.#zoom;
    const dy = (c.y - this.#dragPt.y) / this.#zoom;
    if (!fin(dx) || !fin(dy)) return;

    this.#pan = {
      x: safePan(this.#dragPan.x + dx, this.#dragPan.x),
      y: safePan(this.#dragPan.y + dy, this.#dragPan.y),
    };
    this.#queueRender();
  }

  #onEnd = (e) => {
    if (!(e instanceof TouchEvent)) return;
    if (e.touches.length === 0) {
      this.#isDrag = false;
      this.#queueRender();
    }
  };

  #minZoom = () => {
    const cw = this.clientWidth,
      ch = this.clientHeight;
    if (!pos(cw) || !pos(ch)) return null;
    const sz = this.#imgSize();
    if (!sz) return null;
    const { w, h } = sz;
    if (!pos(w) || !pos(h)) return null;
    const needed = Math.max(cw / w, ch / h) + 0.001;
    return fin(needed) && needed >= MIN_Z ? needed : MIN_Z;
  };

  #imgSize = () => {
    const img = this.#img;
    if (!img) return null;
    const rect = this.getBoundingClientRect();
    const { naturalWidth: nw, naturalHeight: nh } = img;
    const cw = this.clientWidth,
      ch = this.clientHeight;
    if (!pos(nw) || !pos(nh) || !pos(cw) || !pos(ch)) return null;

    const cRatio = rect.width / rect.height;
    const iRatio = nw / nh;
    if (!pos(cRatio) || !pos(iRatio)) return null;

    let w, h;
    if (iRatio > cRatio) {
      w = rect.width;
      h = w / iRatio;
    } else {
      h = rect.height;
      w = h * iRatio;
    }
    return pos(w) && pos(h) ? { w, h } : null;
  };

  #clampBounds() {
    const cw = this.clientWidth,
      ch = this.clientHeight;
    if (!pos(cw) || !pos(ch)) return;

    const mz = this.#minZoom();
    if (!mz) return;

    this.#zoom = clamp(this.#zoom, mz, MAX_Z);
    if (!fin(this.#zoom)) this.#zoom = mz;

    const sz = this.#imgSize();
    if (!sz) return;
    const { w, h } = sz;
    if (!pos(w) || !pos(h)) return;

    const zw = w * this.#zoom,
      zh = h * this.#zoom;
    if (!fin(zw) || !fin(zh)) return;

    const limX = Math.max(0, zw - cw) / (2 * this.#zoom);
    const limY = Math.max(0, zh - ch) / (2 * this.#zoom);
    if (!fin(limX) || !fin(limY)) return;

    this.#pan.x = safePan(clamp(this.#pan.x, -limX, limX));
    this.#pan.y = safePan(clamp(this.#pan.y, -limY, limY));
  }

  #queueRender = () => {
    this.#raf ??= requestAnimationFrame(this.#render);
  };

  #dropFrame() {
    // eslint-disable-next-line eqeqeq
    if (this.#raf != null) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }
  }

  #render = () => {
    this.#raf = null;
    this.#clampBounds();

    this.#zoom = clamp(this.#zoom ?? MIN_Z, MIN_Z, MAX_Z);
    this.#pan.x = safePan(this.#pan.x);
    this.#pan.y = safePan(this.#pan.y);

    this.style.setProperty('--drag-zoom-scale', this.#zoom.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#pan.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#pan.y}px`);
  };

  destroy() {
    this.#ac?.abort();
    this.#ac = null;
    this.#dropFrame();
  }
}

if (!customElements.get('pinch-zoom')) {
  customElements.define('pinch-zoom', TouchZoomContainer);
}
